import { Link } from "@inertiajs/react"
import type { PropsWithChildren } from "react"

import AppLogoIcon from "@/components/app-logo-icon"
import { rootPath } from "@/routes"
import { BRAND_GRADIENT_CLASS } from "@/lib/color-constants"

interface AuthLayoutProps {
  name?: string
  title?: string
  description?: string
}

export default function AuthSimpleLayout({
  children,
  title,
  description,
}: PropsWithChildren<AuthLayoutProps>) {
  return (
    <div className="app-grid-bg relative flex min-h-svh items-center justify-center overflow-hidden p-6 md:p-10">
      <div className="hero-orb absolute top-[10%] left-[8%] h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(53,119,173,0.16)_0%,_transparent_68%)]" />
      <div className="hero-orb absolute right-[10%] bottom-[5%] h-80 w-80 rounded-full bg-[radial-gradient(circle,_rgba(92,203,168,0.2)_0%,_transparent_72%)]" />

      <div className="grid w-full max-w-6xl overflow-hidden rounded-[32px] border border-white/60 bg-white/78 shadow-[0_30px_90px_-40px_rgba(15,23,42,0.45)] backdrop-blur-xl lg:grid-cols-[1.1fr_0.9fr] dark:border-white/8 dark:bg-slate-950/55">
        <div className="hidden border-r border-white/50 bg-[linear-gradient(180deg,rgba(18,45,79,0.98),rgba(23,81,97,0.95))] p-10 text-white lg:flex lg:flex-col lg:justify-between dark:border-white/8">
          <div>
            <Link href={rootPath()} className="inline-flex items-center gap-3">
              <div className="flex size-14 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                <AppLogoIcon className="size-10 text-white" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-[0.26em] text-white/60 uppercase">
                  Wealth Platform
                </div>
                <div className="text-2xl font-semibold tracking-tight">
                  FIREFlow
                </div>
              </div>
            </Link>

            <div className="mt-12 max-w-md">
              <div className="section-kicker border-white/15 bg-white/8 text-white">
                Financial Independence
              </div>
              <h1 className="mt-5 font-serif text-4xl leading-tight tracking-tight">
                Build your dividend strategy and FIRE plan in one place.
              </h1>
              <p className="mt-5 text-base leading-7 text-white/72">
                Track spending, monitor portfolio income, and make faster
                decisions with a workspace designed for long-term wealth.
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            {[
              "Unified view of expenses, goals, and investment progress",
              "FIRE-first analytics powered by real portfolio data",
              "A calmer, more focused interface for daily financial review",
            ].map((item) => (
              <div
                key={item}
                className="rounded-2xl border border-white/10 bg-white/6 px-4 py-3 text-sm text-white/78"
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 md:p-8 lg:p-10">
          <div className="mx-auto flex w-full max-w-sm flex-col gap-8">
            <div className="flex flex-col items-center gap-4 lg:items-start">
              <Link
                href={rootPath()}
                className="flex flex-col items-center gap-3 font-medium lg:hidden"
              >
                <div
                  className={`flex size-14 items-center justify-center rounded-2xl ${BRAND_GRADIENT_CLASS} text-white shadow-lg shadow-slate-900/15`}
                >
                  <AppLogoIcon className="size-10 text-white" />
                </div>
                <span className="sr-only">{title}</span>
              </Link>

              <div className="space-y-2 text-center lg:text-left">
                <h2 className="text-2xl font-semibold tracking-tight">
                  {title}
                </h2>
                <p className="text-muted-foreground text-sm leading-6">
                  {description}
                </p>
              </div>
            </div>

            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
